import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/usr-users/update';

export interface UpdateUserInput {
    name?: string;
    email?: string;
    phone?: string;
    photo_img?: string;
    password?: string;
    password_confirmation?: string;
}

export interface UpdateUserSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: {
        uuid: string;
        [key: string]: any;
    };
    links: Record<string, unknown>;
}

interface UpdateUserValidationErrors {
    [key: string]: string[];
}

/**
 * Requests endpoint to update user profile.
 */
export const useUpdateUser = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<UpdateUserValidationErrors | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<number | null>(null);

    const request = async (input: UpdateUserInput) => {
        setLoading(true);
        setSuccess(false);
        setSuccessMessage(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const headers = getHeaders();

            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify(input),
            });

            const json = await response.json();

            if (!response.ok) {
                const error = new Error(json.message || json.error || 'Erro ao atualizar perfil') as ApiError;
                error.statusCode = response.status;

                if (response.status === 422 && json.errors) {
                    setValidationErrors(json.errors);
                } else {
                    setErrorMessage(json.message || json.error || 'Erro ao atualizar perfil');
                }

                throw error;
            }

            const data: UpdateUserSuccessResponse = json;
            setSuccess(true);
            setSuccessMessage(data.message);
            setErrorMessage(null);
            setValidationErrors(null);

            return data;
        } catch (err: any) {
            setSuccess(false);
            handleCommonErrors(err);
        } finally {
            setLoading(false);
            setLastResponseTime(Date.now());
        }
    };

    return {
        request,
        loading,
        success,
        successMessage,
        errorMessage,
        validationErrors,
        lastResponseTime,
    };
};
